import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { ArrowLeft, FloppyDisk, ChartBar, Key, CheckCircle, XCircle, Plus, Trash, MapPin } from "@phosphor-icons/react";

const LABEL = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";

export default function CustomerEditPage() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sameShip, setSameShip] = useState(false);
  const [pwd, setPwd] = useState("");
  const [settingPwd, setSettingPwd] = useState(false);

  const load = () => axios.get(`${API}/customers/${id}`).then((r) => {
    const pr = r.data.profile;
    setP(pr);
    setSameShip(!pr.ship_address || pr.ship_address === pr.address);
  }).catch(() => setP(false));
  useEffect(() => { load(); }, [id]);

  const set = (patch) => setP((o) => ({ ...o, ...patch }));
  const addShipAddr = () => set({ ship_addresses: [...(p.ship_addresses || []), { label: "", address: "", city: "", province: "", country: "Canada", postal: "" }] });
  const setShipAddr = (i, patch) => set({ ship_addresses: (p.ship_addresses || []).map((a, idx) => (idx === i ? { ...a, ...patch } : a)) });
  const removeShipAddr = (i) => set({ ship_addresses: (p.ship_addresses || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!p.business_name?.trim()) { toast.error(t.businessName + " *"); return false; }
    setSaving(true);
    const ship = sameShip
      ? { ship_address: p.address, ship_city: p.city, ship_province: p.province, ship_country: p.country, ship_postal: p.postal_code }
      : { ship_address: p.ship_address, ship_city: p.ship_city, ship_province: p.ship_province, ship_country: p.ship_country, ship_postal: p.ship_postal };
    try {
      await axios.put(`${API}/customers/${id}`, {
        business_name: p.business_name, entreprise: p.entreprise, succursale: p.business_name,
        first_name: p.first_name, last_name: p.last_name,
        address: p.address, city: p.city, province: p.province, country: p.country, postal_code: p.postal_code,
        ...ship,
        email: p.email, phone: p.phone, fax: p.fax, general_contact: p.general_contact,
        email2: p.email2,
        coop_client_code: p.coop_client_code,
        ship_addresses: (p.ship_addresses || []).filter((a) => a.address || a.label),
      });
      toast.success(t.saved);
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); return false; }
    finally { setSaving(false); }
  };
  const saveAndClose = async () => { if (await save()) navigate("/admin/customers"); };

  const setPassword = async () => {
    if (pwd.length < 6) { toast.error(t.passwordHint); return; }
    setSettingPwd(true);
    try {
      await axios.post(`${API}/admin/customers/${id}/set-password`, { password: pwd });
      toast.success(t.passwordUpdated);
      setPwd(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setSettingPwd(false); }
  };

  if (p === null) return <div className="min-h-screen bg-background"><AppHeader /><div className="p-8 text-muted-foreground">{t.loading}</div></div>;
  if (p === false) return <div className="min-h-screen bg-background"><AppHeader /><div className="p-8 text-muted-foreground">{t.noResults}</div></div>;

  const Actions = () => (
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid="save-button" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
        <FloppyDisk size={16} weight="bold" /> {t.save}
      </button>
      <button data-testid="save-close-button" onClick={saveAndClose} disabled={saving}
        className="inline-flex items-center gap-2 border border-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-60">
        {t.saveAndClose}
      </button>
      <button data-testid="cancel-button" onClick={() => navigate("/admin/customers")}
        className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
        {t.cancel}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <button data-testid="back-to-customers" onClick={() => navigate("/admin/customers")}
          className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors duration-200">
          <ArrowLeft size={16} weight="bold" /> {t.clients}
        </button>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.editCustomer}</div>
            <h1 data-testid="edit-customer-title" className="mt-1 font-display text-2xl font-black tracking-tighter text-foreground">{p.entreprise ? `${p.entreprise} — ${p.business_name}` : p.business_name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="view-report-button" onClick={() => navigate(`/admin/customers/${id}`)}
              className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">
              <ChartBar size={16} weight="bold" /> {t.viewReport}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block"><span className={LABEL}>{t.entreprise}</span>
              <input data-testid="field-entreprise" className={INPUT} value={p.entreprise || ""} onChange={(e) => set({ entreprise: e.target.value })} /></label>
            <label className="block"><span className={LABEL}>{t.succursale} *</span>
              <input data-testid="field-business" className={INPUT} value={p.business_name || ""} onChange={(e) => set({ business_name: e.target.value })} /></label>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label><span className={LABEL}>{t.firstName}</span><input data-testid="field-first" className={INPUT} value={p.first_name || ""} onChange={(e) => set({ first_name: e.target.value })} /></label>
            <label><span className={LABEL}>{t.lastName}</span><input data-testid="field-last" className={INPUT} value={p.last_name || ""} onChange={(e) => set({ last_name: e.target.value })} /></label>
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">{t.billingAddress}</div>
            <label className="block"><span className={LABEL}>{t.address}</span>
              <input data-testid="field-address" className={INPUT} value={p.address || ""} onChange={(e) => set({ address: e.target.value })} /></label>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
              <label><span className={LABEL}>{t.shipCity}</span><input data-testid="field-city" className={INPUT} value={p.city || ""} onChange={(e) => set({ city: e.target.value })} /></label>
              <label><span className={LABEL}>{t.shipProvince}</span><input data-testid="field-province" className={INPUT} value={p.province || ""} onChange={(e) => set({ province: e.target.value })} /></label>
              <label><span className={LABEL}>{t.country}</span><input data-testid="field-country" className={INPUT} value={p.country || ""} onChange={(e) => set({ country: e.target.value })} /></label>
              <label><span className={LABEL}>{t.shipPostal}</span><input data-testid="field-postal" className={`${INPUT} font-mono`} value={p.postal_code || ""} onChange={(e) => set({ postal_code: e.target.value })} /></label>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">{t.shippingAddress}</div>
              <label className="flex items-center gap-2 text-xs font-medium text-foreground">
                <input type="checkbox" data-testid="ship-same-as-billing" checked={sameShip} onChange={(e) => setSameShip(e.target.checked)} />
                {t.shippingSameAsBilling}
              </label>
            </div>
            {!sameShip && (
              <>
                <label className="block"><span className={LABEL}>{t.address}</span>
                  <input data-testid="field-ship-address" className={INPUT} value={p.ship_address || ""} onChange={(e) => set({ ship_address: e.target.value })} /></label>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <label><span className={LABEL}>{t.shipCity}</span><input data-testid="field-ship-city" className={INPUT} value={p.ship_city || ""} onChange={(e) => set({ ship_city: e.target.value })} /></label>
                  <label><span className={LABEL}>{t.shipProvince}</span><input data-testid="field-ship-province" className={INPUT} value={p.ship_province || ""} onChange={(e) => set({ ship_province: e.target.value })} /></label>
                  <label><span className={LABEL}>{t.country}</span><input data-testid="field-ship-country" className={INPUT} value={p.ship_country || ""} onChange={(e) => set({ ship_country: e.target.value })} /></label>
                  <label><span className={LABEL}>{t.shipPostal}</span><input data-testid="field-ship-postal" className={`${INPUT} font-mono`} value={p.ship_postal || ""} onChange={(e) => set({ ship_postal: e.target.value })} /></label>
                </div>
              </>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                <MapPin size={14} weight="bold" /> {t.savedShipAddresses}
              </div>
              <button type="button" data-testid="add-ship-address" onClick={addShipAddr}
                className="inline-flex items-center gap-1.5 border border-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary hover:bg-primary hover:text-white transition-colors duration-200">
                <Plus size={14} weight="bold" /> {t.addShipAddress}
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{t.savedShipAddressesHint}</p>
            {(p.ship_addresses || []).length === 0 && (
              <div data-testid="no-ship-addresses" className="border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">{t.noShipAddresses}</div>
            )}
            <div className="space-y-4">
              {(p.ship_addresses || []).map((a, i) => (
                <div key={i} data-testid={`ship-addr-${i}`} className="border border-border bg-card p-4">
                  <div className="mb-3 flex items-end gap-3">
                    <label className="flex-1"><span className={LABEL}>{t.shipAddressName} *</span>
                      <input data-testid={`ship-addr-label-${i}`} className={INPUT} placeholder={t.shipAddressNamePlaceholder} value={a.label || ""} onChange={(e) => setShipAddr(i, { label: e.target.value })} /></label>
                    <button type="button" data-testid={`remove-ship-addr-${i}`} onClick={() => removeShipAddr(i)}
                      className="inline-flex items-center gap-1 border border-red-300 px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200">
                      <Trash size={14} weight="bold" /> {t.remove}
                    </button>
                  </div>
                  <label className="block"><span className={LABEL}>{t.address}</span>
                    <input data-testid={`ship-addr-address-${i}`} className={INPUT} value={a.address || ""} onChange={(e) => setShipAddr(i, { address: e.target.value })} /></label>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <label><span className={LABEL}>{t.shipCity}</span><input data-testid={`ship-addr-city-${i}`} className={INPUT} value={a.city || ""} onChange={(e) => setShipAddr(i, { city: e.target.value })} /></label>
                    <label><span className={LABEL}>{t.shipProvince}</span><input data-testid={`ship-addr-province-${i}`} className={INPUT} value={a.province || ""} onChange={(e) => setShipAddr(i, { province: e.target.value })} /></label>
                    <label><span className={LABEL}>{t.country}</span><input data-testid={`ship-addr-country-${i}`} className={INPUT} value={a.country || ""} onChange={(e) => setShipAddr(i, { country: e.target.value })} /></label>
                    <label><span className={LABEL}>{t.shipPostal}</span><input data-testid={`ship-addr-postal-${i}`} className={`${INPUT} font-mono`} value={a.postal || ""} onChange={(e) => setShipAddr(i, { postal: e.target.value })} /></label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label><span className={LABEL}>{t.email}</span><input data-testid="field-email" className={INPUT} value={p.email || ""} onChange={(e) => set({ email: e.target.value })} /></label>
            <label><span className={LABEL}>{t.email2}</span><input data-testid="field-email2" className={INPUT} value={p.email2 || ""} onChange={(e) => set({ email2: e.target.value })} /></label>
            <label><span className={LABEL}>{t.generalContact}</span><input data-testid="field-contact" className={INPUT} value={p.general_contact || ""} onChange={(e) => set({ general_contact: e.target.value })} /></label>
            <label><span className={LABEL}>{t.phone}</span><input data-testid="field-phone" className={INPUT} value={p.phone || ""} onChange={(e) => set({ phone: e.target.value })} /></label>
            <label><span className={LABEL}>{t.fax}</span><input data-testid="field-fax" className={INPUT} value={p.fax || ""} onChange={(e) => set({ fax: e.target.value })} /></label>
            <label><span className={LABEL}>{t.coopClientCode}</span><input data-testid="field-coop" className={`${INPUT} font-mono`} value={p.coop_client_code || ""} onChange={(e) => set({ coop_client_code: e.target.value })} /></label>
            <label><span className={LABEL}>{t.accountNumber}</span><input className={`${INPUT} font-mono opacity-60`} value={p.account_number || ""} readOnly /></label>
          </div>

          <div className="border-t border-border pt-6"><Actions /></div>

          {/* Portal account / password */}
          <div className="mt-4 border border-border bg-card p-5">
            <div className="mb-3 flex items-center gap-2">
              <Key size={18} weight="bold" className="text-primary" />
              <h2 className="font-display text-lg font-bold tracking-tight text-foreground">{t.changePassword}</h2>
              <span data-testid="portal-account-status" className={`ml-2 inline-flex items-center gap-1 text-xs font-semibold ${p.has_account ? "text-emerald-600" : "text-muted-foreground"}`}>
                {p.has_account ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                {t.portalAccount}: {p.has_account ? t.hasAccount : t.noPortalAccount}
              </span>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{t.passwordHint} {!p.email && `(${t.email} *)`}</p>
            <div className="flex flex-wrap items-center gap-3">
              <input data-testid="field-password" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder={t.newPassword} className={`${INPUT} max-w-xs`} />
              <button data-testid="set-password-button" onClick={setPassword} disabled={settingPwd || !p.email}
                className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                <Key size={16} weight="bold" /> {t.setPassword}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
